import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getBackendUrl } from '../../../services/get-backend-url';

type OAuthState = {
  sessionKey: string;
};

/**
 * Recover the Connect session key from the `state` a provider echoed back.
 *
 * Most sign-in components send `state` as JSON (`{"sessionKey": "..."}`).
 * Providers that reject non-token characters in `state` (HiBob returns a
 * generic error for braces and quotes) are sent the bare session key instead,
 * so a value that does not parse as JSON is taken as the key itself.
 */
export const parseOAuthStateSessionKey = (
  state: string | null,
): string | null => {
  if (!state) {
    return null;
  }
  try {
    const parsed = JSON.parse(state) as Partial<OAuthState> | null;
    if (parsed && typeof parsed === 'object' && parsed.sessionKey) {
      return parsed.sessionKey;
    }
  } catch {
    // not JSON: fall through to the bare-key form
  }
  return state;
};

export const useUpdateOAuthState = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const oAuthState = {
    state: queryParams.get('state'),
    code: queryParams.get('code'),
    error: queryParams.get('error'),
  };

  const sessionKey = parseOAuthStateSessionKey(oAuthState.state);

  const { isLoading, error: postError } = useQuery({
    queryKey: ['oauth_state_update', sessionKey],
    queryFn: async () => {
      const response = await axios.post(
        `${getBackendUrl()}/auth/oauth/state/${sessionKey}`,
        { oAuthState },
      );

      window.close();

      return response;
    },
    refetchOnWindowFocus: false,
    enabled: !!sessionKey,
  });

  return { isLoading, postError };
};
